import { useEffect, useRef, useState } from "react";
import { Outlet, Link, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import AiPanel from "./AiPanel";
import InsightsChatWidget from "./InsightsChatWidget";
import BillingLockedModal from "./BillingLockedModal";
import NotificationBell from "./NotificationBell";
import ImpersonatePill from "./ImpersonateBanner";
import DemoVisitorPill from "./DemoVisitorPill";
import GlobalTasksButton from "./GlobalTasksButton";
import ProductRail from "./ProductRail";
import ReturnBreadcrumb from "./ReturnBreadcrumb";
import { useNavStyle } from "@/lib/navStyle";
import CommandPalette from "./CommandPalette";
import { MobileTopBar, MobileBottomNav, MobileDrawer } from "./MobileShell";
import { useIsMobile } from "@/lib/useIsMobile";
import { useCompany } from "@/lib/company";
import { useAuth } from "@/lib/auth";
import AskAccountantModal from "@/components/AskAccountantModal";
import { canUseCockpit } from "@/lib/cockpitAccess";
import { TID } from "@/constants/testIds";
import { ChevronDown, LogOut, MessageSquare, Settings2, User, KeyRound, Loader2, X, Search, Building2, MessageCircle, Inbox, Plus, Users, LayoutGrid } from "lucide-react";
import { Toaster, toast } from "sonner";
import { AiFocusProvider } from "@/lib/aiFocus";
import { useActionListener } from "@/lib/createBus";
import { api } from "@/lib/api";
import { useFeedbackUnread } from "@/lib/useFeedbackUnread";
import FeedbackModal from "./FeedbackModal";
import AddCompanyModal from "./AddCompanyModal";

export function CompanySwitcher() {
  const { companies, current, switchCompany } = useCompany();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);
  const containerRef = useRef(null);

  // Reset & auto-focus the search field every time the dropdown opens.
  useEffect(() => {
    if (open) {
      setQuery("");
      setTimeout(() => searchRef.current?.focus(), 40);
    }
  }, [open]);

  // Close on outside-click / Escape — same ergonomics as the profile menu.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!companies?.length) return null;

  // Filter first (search matches company name, business type, owner name
  // or owner email — all searchable per the spec), then group by owner.
  const q = query.trim().toLowerCase();
  const filtered = companies.filter(c => {
    if (!q) return true;
    const hay = [c.name, c.business_type, c.owner_name, c.owner_email]
      .filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });

  // Split Firm Books out first — CPAs' own accounting entity lives at
  // the top of the switcher, above the client-owner groups. `is_firm_books`
  // is set by `enterprises.ensure_firm_books_company_for_pro` at pro
  // signup.
  const firmBooks = filtered.filter(c => c.is_firm_books === true);
  const clientCompanies = filtered.filter(c => c.is_firm_books !== true);

  // Group into { ownerKey: {label, email, companies:[]} }. Owners with
  // no name fall back to their email; owners with neither (edge case)
  // land in a "Unassigned" bucket so nothing silently disappears.
  const groups = new Map();
  for (const c of clientCompanies) {
    const key = c.owner_email || c.owner_name || "__unassigned__";
    if (!groups.has(key)) {
      groups.set(key, {
        label: c.owner_name || c.owner_email || "Unassigned",
        email: c.owner_email || "",
        companies: [],
      });
    }
    groups.get(key).companies.push(c);
  }
  // Sort owner buckets so the current company's owner is first —
  // more likely what you're switching within.
  const orderedGroups = [...groups.entries()].sort(([a], [b]) => {
    const isCurA = current?.owner_email === a || current?.owner_name === a;
    const isCurB = current?.owner_email === b || current?.owner_name === b;
    if (isCurA && !isCurB) return -1;
    if (isCurB && !isCurA) return 1;
    return groups.get(a).label.localeCompare(groups.get(b).label);
  });

  return (
    <div className="relative" ref={containerRef}>
      <button
        data-testid={TID.companySwitcher}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-300 bg-white text-sm"
      >
        <span className="font-heading font-semibold">{current?.name || "Select company"}</span>
        <ChevronDown size={14} className="text-slate-500" />
      </button>
      {open && (
        <div
          className="absolute z-50 top-full mt-1 w-80 rounded-md border bg-white shadow-lg py-1"
          data-testid="company-switcher-dropdown"
        >
          <div className="px-2 pt-1 pb-2 border-b">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search company or owner…"
                data-testid="company-switcher-search"
                className="w-full text-xs border rounded px-7 py-1.5 focus:outline-none focus:border-cyan-400"
              />
            </div>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {orderedGroups.length === 0 && firmBooks.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-slate-500" data-testid="company-switcher-empty">
                No matches for “{query}”.
              </div>
            )}
            {/* Firm Books section — CPAs' own accounting entities.
                Rendered at the very top with a distinct label + Building
                icon so it's visually separated from client-owner groups. */}
            {firmBooks.length > 0 && (
              <div className="py-1 border-b" data-testid="switcher-group-firm-books">
                <div className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-widest text-cyan-700 flex items-center gap-1.5">
                  <Building2 size={10} className="text-cyan-500" />
                  Firm books
                </div>
                {firmBooks.map(c => {
                  const active = current?.id === c.id;
                  return (
                    <button
                      key={c.id}
                      data-testid={`${TID.companySwitcherOption}-${c.id}`}
                      onClick={() => { switchCompany(c.id); setOpen(false); }}
                      className={`w-full text-left px-3 py-1.5 hover:bg-cyan-50/50 text-sm flex items-center justify-between ${active ? "bg-cyan-50" : ""}`}
                    >
                      <div className="min-w-0">
                        <div className={`font-medium truncate ${active ? "text-cyan-800" : "text-slate-900"}`}>{c.name}</div>
                        <div className="text-[11px] text-slate-500 truncate">Firm's own accounting</div>
                      </div>
                      {active && <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-semibold ml-2 flex-shrink-0">Current</div>}
                    </button>
                  );
                })}
              </div>
            )}
            {orderedGroups.map(([key, group]) => (
              <div key={key} className="py-1" data-testid={`switcher-group-${key}`}>
                <div className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                  <User size={10} className="text-slate-400" />
                  {group.label}
                  {group.email && group.email !== group.label && (
                    <span className="text-slate-400 font-normal normal-case tracking-normal truncate">· {group.email}</span>
                  )}
                </div>
                {group.companies.map(c => {
                  const active = current?.id === c.id;
                  return (
                    <button
                      key={c.id}
                      data-testid={`${TID.companySwitcherOption}-${c.id}`}
                      onClick={() => { switchCompany(c.id); setOpen(false); }}
                      className={`w-full text-left px-3 py-1.5 hover:bg-slate-50 text-sm flex items-center justify-between ${active ? "bg-cyan-50" : ""}`}
                    >
                      <div className="min-w-0">
                        <div className={`font-medium truncate ${active ? "text-cyan-800" : "text-slate-900"}`}>{c.name}</div>
                        <div className="text-[11px] text-slate-500 truncate">{c.business_type || "—"}</div>
                      </div>
                      {active && <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-semibold ml-2 flex-shrink-0">Current</div>}
                    </button>
                  );
                })}
              </div>
            ))}
            {/* "+ Add new company" footer — always visible at the
                 bottom of the switcher so any CPA can spin up a new
                 client without hunting through the Clients page.
                 Navigates to /clients?new=1 which auto-opens the
                 NewClientModal on that page. */}
            {/* Owners of 2+ companies get the cross-company rollup */}
            {user?.role === "client" && companies.length >= 2 && (
              <button
                type="button"
                onClick={() => { setOpen(false); navigate("/portfolio"); }}
                className="w-full text-left px-3 py-2 text-sm text-slate-800 hover:bg-slate-50 flex items-center gap-1.5 border-t border-slate-100 font-medium"
                data-testid="company-switcher-portfolio"
              >
                <LayoutGrid size={14} /> All my businesses · portfolio
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                if (user?.role === "client") setAddOpen(true);
                else navigate("/pro/clients?new=1");
              }}
              className="w-full text-left px-3 py-2 text-sm text-cyan-700 hover:bg-cyan-50 flex items-center gap-1.5 border-t border-slate-100 sticky bottom-0 bg-white"
              data-testid="company-switcher-add-new"
            >
              <Plus size={14} /> Add new company
            </button>
          </div>
        </div>
      )}
      <AddCompanyModal open={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  );
}

export function ProfileMenu() {
  // Profile chip in the topbar. Replaces the previous "email · sign out"
  // strip with an avatar-initials pill that opens a dropdown for Settings,
  // Change password, and Sign out. Pros/superadmins additionally get a
  // link to the enterprise-branding settings surface.
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [fbOpen, setFbOpen] = useState(false);
  const ref = useRef(null);
  const { reporter: unreadReporter, admin: unreadAdmin } = useFeedbackUnread({
    isSuperadmin: user?.role === "superadmin",
  });
  const totalUnread = (unreadReporter || 0) + (unreadAdmin || 0);

  // Close on outside-click / Escape — cheap dropdown ergonomics.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;
  const name = user.name || user.email?.split("@")[0] || "User";
  const initials = name.split(/\s+/).map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const isPro = ["pro", "superadmin"].includes(user.role);
  const isPartner = user.role === "partner";
  // Partners get the same profile-menu shape as Pros (Settings, Team,
  // Change password, Sign out) because they inherit every Pro power.
  // The Settings link routes to `/pro/settings` — that page already
  // reads `branding.firm_name / subdomain / logo_url / primary_color`
  // off the user doc, and Partners have the exact same shape, so it
  // Just Works with a role-aware heading swap ("Partner settings"
  // instead of "Enterprise settings").
  const showProMenu = isPro || isPartner;

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="profile-menu-trigger"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-2 py-1 rounded-full hover:bg-slate-100 transition relative"
        title={user.email}
      >
        <span className="w-8 h-8 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center">
          {initials || <User size={14} />}
        </span>
        {totalUnread > 0 && (
          <span
            data-testid="profile-menu-unread-dot"
            className="absolute -top-0.5 -left-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center leading-none"
            title={`${totalUnread} unread`}
          >
            {totalUnread > 9 ? "9+" : totalUnread}
          </span>
        )}
        <span className="hidden md:flex flex-col items-start leading-tight">
          <span className="text-xs font-semibold text-slate-900">{name}</span>
          <span className="text-[10px] text-slate-500">{user.email}</span>
        </span>
        <ChevronDown size={14} className="text-slate-500" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-64 rounded-md border bg-white shadow-lg py-1 z-50"
          data-testid="profile-menu"
        >
          <div className="px-3 py-2 border-b">
            <div className="text-sm font-semibold truncate">{name}</div>
            <div className="text-[11px] text-slate-500 truncate">{user.email}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">
              {user.role || "user"}
            </div>
          </div>
          {showProMenu && (
            <Link
              to="/pro/settings"
              onClick={() => setOpen(false)}
              data-testid="profile-menu-settings"
              className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50"
            >
              <Settings2 size={14} className="text-slate-500" /> Settings
            </Link>
          )}
          {showProMenu && (
            <Link
              to="/pro/team"
              onClick={() => setOpen(false)}
              data-testid="profile-menu-team"
              className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50"
            >
              <User size={14} className="text-slate-500" /> {isPartner ? "Partner staff" : "Firm staff"}
            </Link>
          )}
          {!showProMenu && user?.role !== "superadmin" && (
            <Link
              to="/company-team"
              onClick={() => setOpen(false)}
              data-testid="profile-menu-team"
              className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50"
            >
              <User size={14} className="text-slate-500" /> Team &amp; permissions
            </Link>
          )}
          <button
            onClick={() => { setOpen(false); setPwOpen(true); }}
            data-testid="profile-menu-change-password"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50"
          >
            <KeyRound size={14} className="text-slate-500" /> Change password
          </button>
          <div className="my-1 border-t border-slate-100" />
          <button
            onClick={() => { setOpen(false); setFbOpen(true); }}
            data-testid="profile-menu-feedback"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50"
          >
            <MessageCircle size={14} className="text-slate-500" /> Send feedback
          </button>
          <Link
            to="/feedback/mine"
            onClick={() => setOpen(false)}
            data-testid="profile-menu-my-feedback"
            className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50"
          >
            <Inbox size={14} className="text-slate-500" /> My feedback
            {unreadReporter > 0 && (
              <span
                data-testid="profile-menu-my-feedback-count"
                className="ml-auto min-w-[18px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center leading-none"
              >
                {unreadReporter > 9 ? "9+" : unreadReporter}
              </span>
            )}
          </Link>
          {showProMenu && (
            <Link
              to="/pro/affiliates"
              onClick={() => setOpen(false)}
              data-testid="profile-menu-affiliates"
              className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50"
            >
              <Users size={14} className="text-slate-500" /> Affiliates
            </Link>
          )}
          {user.role === "superadmin" && (
            <Link
              to="/admin/feedback"
              onClick={() => setOpen(false)}
              data-testid="profile-menu-admin-feedback"
              className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50"
            >
              <Inbox size={14} className="text-slate-500" /> Feedback inbox
              {unreadAdmin > 0 && (
                <span
                  data-testid="profile-menu-admin-feedback-count"
                  className="ml-auto min-w-[18px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center leading-none"
                >
                  {unreadAdmin > 9 ? "9+" : unreadAdmin}
                </span>
              )}
            </Link>
          )}
          <div className="my-1 border-t border-slate-100" />
          <button
            data-testid={TID.signoutBtn}
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-red-700"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      )}
      {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}
      {fbOpen && <FeedbackModal onClose={() => setFbOpen(false)} />}
    </div>
  );
}


function ChangePasswordModal({ onClose }) {
  // Small self-service password rotation modal. Verifies the current
  // password server-side (backend uses bcrypt), so a leaked JWT alone
  // can't hijack this flow. Existing sessions stay valid by design —
  // no forced logout after rotation.
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (next.length < 8) return toast.error("New password must be 8+ characters.");
    if (next !== confirm) return toast.error("New passwords don't match.");
    if (next === current) return toast.error("New password must be different from your current one.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated.");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't change password.");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="change-password-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-heading font-semibold flex items-center gap-2">
            <KeyRound size={16} className="text-cyan-600" /> Change password
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X size={14} /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div>
            <label className="text-xs text-slate-600">Current password</label>
            <input
              type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
              autoFocus
              data-testid="cp-current"
              className="w-full mt-1 border rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">New password</label>
            <input
              type="password" value={next} onChange={(e) => setNext(e.target.value)}
              data-testid="cp-new"
              className="w-full mt-1 border rounded-md px-3 py-2"
              placeholder="8+ characters"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">Confirm new password</label>
            <input
              type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              data-testid="cp-confirm"
              className="w-full mt-1 border rounded-md px-3 py-2"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-md border text-sm">Cancel</button>
          <button
            onClick={submit} disabled={busy || !current || !next || !confirm}
            data-testid="cp-submit"
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-cyan-600 text-white text-sm disabled:opacity-50 hover:bg-cyan-700"
          >
            {busy && <Loader2 size={13} className="animate-spin" />} Update password
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [aiCollapsed, setAiCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const isMobile = useIsMobile();
  const { user } = useAuth();
  // Row-level "Ask AI" buttons emit `ai-open` — expand the panel when it fires.
  useActionListener("ai-open", () => setAiCollapsed(false));
  // Onboarding step 1 toggles the panel closed via `ai-close` when the
  // user opts out of AI-assisted onboarding.
  useActionListener("ai-close", () => setAiCollapsed(true));

  // Nav style 4 (product accordion) has all products visible IN the
  // sidebar already — the vertical product rail becomes redundant.
  const [navStyle] = useNavStyle();
  const showRail = navStyle !== "accordion";
  // Affiliate-only accounts get a stripped-down chrome: no sidebar, no
  // company switcher, no AI panel, no billing modal. They only ever see
  // the Share page — everything else is unreachable via <Protected/>.
  const isAffiliate = user?.role === "affiliate";

  // Auto-collapse the AI panel on mobile boot — the phone viewport
  // can't afford to have it eating half the screen the moment the
  // app mounts. Chat tab in the bottom nav re-opens it via
  // `ai-open` event.
  useEffect(() => {
    if (isMobile) setAiCollapsed(true);
  }, [isMobile]);

  if (isAffiliate) {
    return (
      <div className="flex flex-col h-screen overflow-hidden bg-[#F5F6F8]">
        <header className="h-16 shrink-0 border-b bg-white flex items-center px-6 gap-4">
          <div className="ml-auto flex items-center gap-3">
            <div className="relative z-[1000]">
              <ProfileMenu />
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-6 md:p-8">
          <ReturnBreadcrumb />
          <Outlet />
        </main>
        <Toaster position="bottom-right" />
      </div>
    );
  }

  // -------------------------------------------------------------------
  // Mobile shell — completely different chrome (top bar + drawer +
  // bottom nav) so the small viewport isn't fighting a 320px sidebar
  // + 40px product rail + 380px AI panel. AI panel becomes a
  // full-height overlay when the bottom-nav Chat tab opens it.
  // -------------------------------------------------------------------
  if (isMobile) {
    return (
      <AiFocusProvider>
        <div className="flex flex-col min-h-screen bg-[#F5F6F8]">
          <MobileTopBar onOpenDrawer={() => setMobileDrawerOpen(true)} />
          <MobileDrawer
            open={mobileDrawerOpen}
            onClose={() => setMobileDrawerOpen(false)}
          />
          {/* Reserve 64px + safe-area for the bottom nav so page
              content never gets hidden under it. */}
          <main
            className="flex-1 overflow-y-auto p-4"
            style={{ paddingBottom: aiCollapsed ? "calc(72px + env(safe-area-inset-bottom))" : "calc(48vh + 80px + env(safe-area-inset-bottom))" }}
          >
            <ReturnBreadcrumb />
            <Outlet />
          </main>
          {/* While the assistant sheet is open it takes over the bottom
              edge (nav hidden) so the conversation gets the room. */}
          {aiCollapsed && <MobileBottomNav />}
          <AiPanel collapsed={aiCollapsed} onToggle={() => setAiCollapsed(!aiCollapsed)} />
          <BillingLockedModal />
          <Toaster position="top-center" />
        </div>
      </AiFocusProvider>
    );
  }

  return (
    <AiFocusProvider>
      <div className="flex h-screen overflow-hidden bg-[#F5F6F8]">
        {showRail && <ProductRail />}
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />

        <div className="flex-1 flex flex-col min-w-0">
          <header
            className="h-16 shrink-0 border-b flex items-center px-6 gap-4"
            style={{ backgroundColor: "var(--brand-topbar-bg)" }}
            data-testid="app-topbar"
          >
            {/* CompanySwitcher gets z-[1000] so it stays clickable when
                the BillingLockedModal (z-[999]) is up — clients who own
                multiple companies can escape a locked one via the
                dropdown. The rest of the header (profile menu, AI
                toggle, alerts bell) stays at the header's normal stack
                level so it visually dims under the modal backdrop and
                doesn't distract from the "Activate & pay" CTA. */}
            <div className="relative z-[1000]">
              <CompanySwitcher />
            </div>
            <div className="ml-auto flex items-center gap-3">
              <ImpersonatePill />
              <DemoVisitorPill />
              {aiCollapsed && (
                <button
                  data-testid={TID.aiPanelToggle + "-header"}
                  onClick={() => setAiCollapsed(false)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs"
                >
                  <MessageSquare size={13} /> Assistant
                </button>
              )}
              <AskAccountantHeaderButton />
              <NotificationBell />
              <GlobalTasksButton />
              {/* Profile menu also elevated so the client can Sign out
                  / Change password even while the BillingLockedModal is
                  up. Assistant toggle and alerts bell stay at default
                  stack so they dim under the backdrop. */}
              <div className="relative z-[1000]">
                <ProfileMenu />
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-auto p-6 md:p-8">
            <ReturnBreadcrumb />
            <Outlet />
          </main>
        </div>

        <AiPanel collapsed={aiCollapsed} onToggle={() => setAiCollapsed(!aiCollapsed)} />
        <InsightsChatWidget />
        <BillingLockedModal />
        <CommandPalette />
        <Toaster position="bottom-right" />
      </div>
    </AiFocusProvider>
  );
}


// "Ask my accountant" — only for client-side users whose current company
// was set up by / is managed by an accounting professional.
function AskAccountantHeaderButton() {
  const { user } = useAuth();
  const { current } = useCompany();
  const [open, setOpen] = useState(false);
  if (!user || canUseCockpit(user) || !current?.has_pro) return null;
  return (
    <>
      <button
        data-testid="ask-accountant-header-btn"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-800 text-xs font-medium hover:bg-slate-50"
      >
        <MessageSquare size={13} /> Ask my accountant
      </button>
      <AskAccountantModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
